/* eslint-disable react-hooks/rules-of-hooks, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from "react";
import "./styles/app.css";
import { NAV_HIDDEN_PAGE_IDS } from "./features";
import IdentityPage from "./identity/IdentityPage";
import { canManageIdentities, canAccessHrTools, grantCovers, hasBroadOpsScope, isEmployeeRole, normalizeSessionRole } from "./identity/access";
import { stashCurrentOpsSessionIfNeeded, takeImpersonateTokenFromLocation } from "./identity/impersonation";
import {
  getOpsToken,
  setOpsToken,
  clearOpsToken,
  getOpsImpersonatorToken,
  clearOpsImpersonatorToken,
  fetchOpsAuthMeWithRetry,
  isAuthRejectionStatus,
} from "./api/client";
import StoresProvider, { usePgStores } from "./stores/StoresProvider";
import { useLeaveRequests } from "./hooks";
import { useProcureOrdersBadge } from "./hooks/useProcureOrdersBadge";
import { useIsMobile } from "./hooks/useIsMobile";
import { BRAND_THEMES, resolveTheme, applyThemeDom } from "./theme";
import { leaveStoreNamesForUser } from "./storeBrand";
import { opsUserFromAuthPayload } from "./lib/userDisplay";
import {
  defaultPageForUser, landingPageForUser, pageFromPathname, authModeFromPathname,
  pathForAppPage, pathForAuthMode, rememberHistoryEntry, replaceBrowserPath, pageKeepsSearch, canAccessRecipes, canAccessEodStock,
} from "./routing/paths";
import { rotatePageAbort } from "./routing/pageAbort";
import { getBuilderHref, runWithLeaveGuard } from "./routing/leaveGuard";
import { leaveRequestInStoreScope } from "./lib/employeeStore";
import { searchCarryingStore } from "./lib/selectedStore";
import { canBuildSchedule } from "./lib/scheduleLadder";
import { AuthScreen } from "./auth/AuthScreen";
import { AppShell } from "./layout/AppShell";
import { AnnouncementsPage } from "./features/announcements/AnnouncementsPage";
import { ScheduleViewerPage } from "./features/schedule/ScheduleViewerPage";
import { ScheduleBuilderPage } from "./features/schedule/ScheduleBuilderPage";
import { LeaveRequestsPage } from "./features/leave/LeaveRequestsPage";
import { AcademyPage } from "./features/academy/AcademyPage";
import { PortioningPage } from "./features/portioning/PortioningPage";
import { BonusCalculatorPage } from "./features/bonus/BonusCalculatorPage";
import { AdminPage } from "./features/admin/AdminPage";
import { RecipesPage } from "./features/recipes/RecipesPage";
import { EndOfDayStockPage } from "./features/eod-stock/EndOfDayStockPage";
import { ProcureIQPage } from "./features/procureiq/ProcureIQPage";
import { ProcureOrdersPage } from "./features/procureiq/ProcureOrdersPage";
import { ProcureOrderViewPage } from "./features/procureiq/ProcureOrderViewPage";
import { HRDashboardPage } from "./features/hr-dashboard/HRDashboardPage";
import { SettingsPage } from "./features/settings/SettingsPage";
import { ModalBodyScrollLock } from "./ui";

function AppContent() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [page, setPage] = useState("announcements");
  const [lang, setLang] = useState("en");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [activeTheme, setActiveTheme] = useState(BRAND_THEMES.stiq);
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try {
      return localStorage.getItem("pg_nav_collapsed") === "true";
    } catch {
      return false;
    }
  });
  const [authMode, setAuthMode] = useState(() => {
    try {
      return authModeFromPathname(window.location.pathname);
    } catch {
      return "login";
    }
  });
  const [resetToken, setResetToken] = useState("");
  const isMobileViewport = useIsMobile();

  const applyTheme = useCallback((theme) => {
    applyThemeDom(theme);
    setActiveTheme(theme);
  }, []);

  const { storeNames: pgStoreNames, storeRecords: pgStoreRecords, brands: pgBrands } = usePgStores();

  useEffect(() => {
    applyTheme(resolveTheme(user, pgStoreRecords, pgBrands));
  }, [user, pgStoreRecords, pgBrands, applyTheme]);

  useEffect(() => {
    if (NAV_HIDDEN_PAGE_IDS.has(page)) {
      setPage(user ? defaultPageForUser(user) : "announcements");
    }
  }, [page, user]);

  useEffect(() => {
    let cancelled = false;

    function applySession(data) {
      const session = opsUserFromAuthPayload(data) || data;
      const { access_token: _at, ...rest } = session;
      setUser(rest);
      setPage(landingPageForUser(rest));
      setAuthReady(true);
    }

    async function restoreSession(token) {
      try {
        const data = await fetchOpsAuthMeWithRetry(token);
        if (!cancelled) applySession(data);
        return;
      } catch (err) {
        if (cancelled || !isAuthRejectionStatus(err?.status)) {
          // Deploy / network blip: keep the JWT so a later refresh can recover.
          if (!cancelled) setAuthReady(true);
          return;
        }
      }

      const original = getOpsImpersonatorToken();
      if (original && original !== token) {
        clearOpsImpersonatorToken();
        setOpsToken(original);
        try {
          const data = await fetchOpsAuthMeWithRetry(original);
          if (!cancelled) applySession(data);
          return;
        } catch (err) {
          if (cancelled) return;
          if (!isAuthRejectionStatus(err?.status)) {
            setAuthReady(true);
            return;
          }
        }
      }

      if (!cancelled) {
        clearOpsToken();
        setAuthReady(true);
      }
    }

    const handed = takeImpersonateTokenFromLocation();
    if (handed) {
      stashCurrentOpsSessionIfNeeded();
      setOpsToken(handed);
    }
    const token = getOpsToken();
    if (!token) {
      setAuthReady(true);
      return undefined;
    }
    void restoreSession(token);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const token = (params.get("reset_token") || "").trim();
      if (token) {
        setResetToken(token);
        setAuthMode("reset");
      }
    } catch {}
  }, []);

  const pageRef = useRef(page);
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  const goToPage = useCallback((next) => {
    if (!next || next === pageRef.current) return;
    if (pageRef.current === "builder" && next !== "builder") {
      runWithLeaveGuard(() => setPage(next));
      return;
    }
    setPage(next);
  }, []);

  useLayoutEffect(() => {
    rotatePageAbort();
  }, [page]);

  useLayoutEffect(() => {
    if (!authReady) return;
    if (!user) {
      const search =
        authMode === "reset" && resetToken
          ? `?reset_token=${encodeURIComponent(resetToken)}`
          : "";
      replaceBrowserPath(pathForAuthMode(authMode), search);
      return;
    }
    const path = pathForAppPage(page);
    const search = pageKeepsSearch(page) ? searchCarryingStore(page, window.location.search) : "";
    const onThisPage = pageFromPathname(window.location.pathname) === page;
    rememberHistoryEntry(path, search, { push: !onThisPage });
  }, [authReady, user, page, authMode, resetToken]);

  useEffect(() => {
    function onPop() {
      if (!user) {
        setAuthMode(authModeFromPathname(window.location.pathname));
        return;
      }
      const next = pageFromPathname(window.location.pathname);
      if (!next || NAV_HIDDEN_PAGE_IDS.has(next)) return;
      const from = pageRef.current;
      if (from === next) return;
      if (from === "builder" && next !== "builder") {
        const restoreHref = getBuilderHref();
        runWithLeaveGuard(
          () => setPage(next),
          () => {
            if (restoreHref) {
              window.history.pushState(window.history.state, "", restoreHref);
            }
          },
        );
        return;
      }
      setPage(next);
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [user]);

  const userRole = normalizeSessionRole(user?.role);
  const isFullAdmin = userRole === "admin";
  const isBrandManager = userRole === "brand_manager";
  const isAdminOrBrandManager = isFullAdmin || isBrandManager;
  const isManager = canBuildSchedule(user);
  const canAdmin =
    isAdminOrBrandManager
    || userRole === "ops_manager"
    || (grantCovers(user?.grants, "ops", "announcements", "edit") && hasBroadOpsScope(user));
  const firstLeaveStore = pgStoreNames[0] || "";
  const leaveScopedStoreNames = useMemo(
    () => leaveStoreNamesForUser(user, pgStoreNames, pgStoreRecords, pgBrands),
    [user, pgStoreNames, pgStoreRecords, pgBrands]
  );
  const leaveRequestStore = hasBroadOpsScope(user)
    ? "All Stores"
    : (user?.store === "All Stores" || !user?.store ? firstLeaveStore : user?.store);
  const leaveRequests = useLeaveRequests(leaveRequestStore, user, leaveScopedStoreNames, pgStoreRecords, pgBrands);
  const pendingLeave = leaveRequests.loading ? 0 : leaveRequests.reqs.filter((req) => {
    if (req.status !== "pending") return false;
    if (isEmployeeRole(user)) {
      const sameEmail = user?.email && req.employee_email?.toLowerCase() === user.email.toLowerCase();
      const sameName = req.employee_name?.toUpperCase() === user?.name?.toUpperCase();
      return Boolean(sameEmail || sameName);
    }
    if (userRole === "store_manager") return req.store_id === user?.store;
    if (hasBroadOpsScope(user)) {
      return leaveRequestInStoreScope(req, leaveScopedStoreNames, "All Stores");
    }
    return false;
  }).length;
  // Refresh when leaving Procure Orders (after verify) and while browsing.
  const procureOrdersBadge = useProcureOrdersBadge(user, {
    refreshKey: page === "procure-orders" ? "on-page" : `away-${page}`,
  });

  useEffect(() => {
    try {
      localStorage.setItem("pg_nav_collapsed", String(navCollapsed));
    } catch {}
  }, [navCollapsed]);

  useEffect(() => {
    if (!user) return;
    if (page === "hr-dashboard" && !canAccessHrTools(user)) {
      setPage(defaultPageForUser(user));
      return;
    }
    if (page === "recipes" && !canAccessRecipes(user)) {
      setPage(defaultPageForUser(user));
    }
    if (page === "identity" && !canManageIdentities(user)) {
      setPage(defaultPageForUser(user));
    }
  }, [user, page]);

  function handleLogout() {
    clearOpsToken();
    setUser(null);
  }

  function requestLogout() {
    runWithLeaveGuard(() => handleLogout());
  }

  function handleStopImpersonating() {
    const original = getOpsImpersonatorToken();
    clearOpsImpersonatorToken();
    if (!original) {
      handleLogout();
      return;
    }
    setOpsToken(original);
    void fetchOpsAuthMeWithRetry(original)
      .then((data) => {
        const session = opsUserFromAuthPayload(data) || data;
        const { access_token: _at, ...rest } = session;
        setUser(rest);
        setPage(landingPageForUser(rest));
      })
      .catch((err) => {
        // Keep the restored JWT on deploy blips; only wipe on real auth failure.
        if (isAuthRejectionStatus(err?.status)) handleLogout();
      });
  }

  function handleAuthenticated(sessionUser, landingPage) {
    setUser(sessionUser);
    setPage(landingPage);
  }

  if (!authReady || !user) {
    return (
      <AuthScreen
        activeTheme={activeTheme}
        lang={lang}
        authReady={authReady}
        authMode={authMode}
        setAuthMode={setAuthMode}
        resetToken={resetToken}
        setResetToken={setResetToken}
        onAuthenticated={handleAuthenticated}
      />
    );
  }

  const defaultNavItems = [
    { id: "announcements", label: "Announcements", labelGr: "Ανακοινώσεις", icon: "megaphone", showFor: "announcements" },
    { id: "schedule", label: "My Schedule", labelGr: "Πρόγραμμα", icon: "calendar", showFor: "employee" },
    { id: "builder", label: "Schedule", labelGr: "Πρόγραμμα", icon: "pencil", showFor: "manager" },
    { id: "leave", label: "Leave", labelGr: "Άδειες", icon: "leaf", badge: pendingLeave, showFor: "leave" },
    { id: "eod-stock", label: "End of Day Stock", labelGr: "Απόθεμα ημέρας", icon: "clipboard", showFor: "eod-stock" },
    { id: "admin", label: "People & Stores", labelGr: "Άνθρωποι & Καταστήματα", icon: "shield", showFor: "admin" },
    { id: "identity", label: "Identity", labelGr: "Ταυτότητα", icon: "people", showFor: "identity" },
    { id: "hr-dashboard", label: "HR Dashboard", labelGr: "Πίνακας HR", icon: "people", showFor: "foxline" },
    { id: "recipes", label: "Recipes", labelGr: "Συνταγές", icon: "book", showFor: "recipes" },
    { id: "procureiq", label: "ProcureIQ", labelGr: "ProcureIQ", icon: "cart", showFor: "procure" },
    {
      id: "procure-orders",
      label: "Procure Orders",
      labelGr: "Παραγγελίες προμηθειών",
      icon: "clipboard",
      badge: procureOrdersBadge,
      showFor: "procure-orders",
    },
    { id: "settings", label: "Settings", labelGr: "Ρυθμίσεις", icon: "settings" },
  ].filter((item) => {
    if (NAV_HIDDEN_PAGE_IDS.has(item.id)) return false;
    if (item.showFor === "announcements") {
      return grantCovers(user.grants, "ops", "announcements", "view") || !user.grants?.length;
    }
    if (item.showFor === "leave") {
      return grantCovers(user.grants, "ops", "leave", "view") || !user.grants?.length;
    }
    if (item.showFor === "employee") {
      return (
        isEmployeeRole(user)
        && (grantCovers(user.grants, "ops", "schedule", "view") || !user.grants?.length)
      );
    }
    if (item.showFor === "manager") {
      return isManager && !isEmployeeRole(user);
    }
    if (item.showFor === "admin") return canAdmin;
    if (item.showFor === "identity") return canManageIdentities(user);
    if (item.showFor === "foxline") return canAccessHrTools(user);
    if (item.showFor === "recipes") return canAccessRecipes(user);
    if (item.showFor === "procure") {
      return (
        grantCovers(user.grants, "ops", "procureiq", "view")
        || ["store_manager", "admin", "brand_manager", "ops_manager", "hr"].includes(userRole)
      );
    }
    if (item.showFor === "procure-orders") {
      return (
        grantCovers(user.grants, "ops", "procureiq", "view")
        || ["store_manager", "admin", "brand_manager", "hr", "manager"].includes(userRole)
      );
    }
    if (item.showFor === "eod-stock") return canAccessEodStock(user);
    return true;
  });

  const navItems = defaultNavItems;

  // Order deep-link tab: no sidebar / shell — only the order + Close / Show on Supy.
  if (page === "procure-order") {
    return <ProcureOrderViewPage lang={lang} />;
  }

  function renderPage() {
    if (page === "announcements") return <AnnouncementsPage user={user} lang={lang} />;
    if (page === "schedule") return <ScheduleViewerPage user={user} lang={lang} />;
    if (page === "builder") return <ScheduleBuilderPage user={user} lang={lang} allUsers={allUsers} />;
    if (page === "leave") return <LeaveRequestsPage user={user} lang={lang} allUsers={allUsers} leaveRequests={leaveRequests} />;
    if (page === "eod-stock") return <EndOfDayStockPage user={user} lang={lang} />;
    if (page === "academy") return <AcademyPage user={user} lang={lang} />;
    if (page === "admin") return <AdminPage user={user} lang={lang} allUsers={allUsers} setAllUsers={setAllUsers} />;
    if (page === "identity") return <IdentityPage lang={lang} sessionUser={user} onLocalImpersonation={handleAuthenticated} />;
    if (page === "portioning") return <PortioningPage user={user} lang={lang} />;
    if (page === "bonus") return <BonusCalculatorPage user={user} lang={lang} />;
    if (page === "procureiq") return <ProcureIQPage user={user} lang={lang} />;
    if (page === "procure-orders") return <ProcureOrdersPage user={user} lang={lang} />;
    if (page === "recipes") return <RecipesPage user={user} lang={lang} />;
    if (page === "hr-dashboard") return <HRDashboardPage user={user} lang={lang} />;
    if (page === "settings") return <SettingsPage user={user} lang={lang} setLang={setLang} />;
    return null;
  }

  return (
    <StoresProvider user={user}>
      <ModalBodyScrollLock />
      <AppShell
        user={user}
        page={page}
        setPage={goToPage}
        lang={lang}
        activeTheme={activeTheme}
        isMobileViewport={isMobileViewport}
        navCollapsed={navCollapsed}
        setNavCollapsed={setNavCollapsed}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        navItems={navItems}
        onLogout={requestLogout}
        onStopImpersonating={handleStopImpersonating}
      >
        {renderPage()}
      </AppShell>
    </StoresProvider>
  );
}

export default function App() {
  return <AppContent />;
}
